/**
 * Report Content Dialog Component
 * Dialog for reporting content (posts, discussions, etc.) that creates a ticket
 */

import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
} from '@mui/material'
import { TicketType, TicketTypeLabels, TargetType, CreateTicketRequest } from '@api/types/tickets'
import { TicketTypeIcon } from './TicketTypeIcon'

interface ReportContentDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (request: CreateTicketRequest) => Promise<void>
  targetType: TargetType
  targetId: string
  isSubmitting?: boolean
  contentPreview?: string
}

// Map target types to appropriate ticket types
const contentReportTypes = [
  TicketType.ContentInappropriate,
  TicketType.ContentMisleading,
  TicketType.ContentSpam,
  TicketType.ContentHarassment,
  TicketType.Other,
]

export function ReportContentDialog({
  open,
  onClose,
  onSubmit,
  targetType,
  targetId,
  isSubmitting = false,
  contentPreview,
}: ReportContentDialogProps) {
  const [type, setType] = useState<TicketType>(TicketType.ContentInappropriate)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    setError(null)

    if (!title.trim()) {
      setError('Please provide a title')
      return
    }

    if (!description.trim()) {
      setError('Please provide a description of the issue')
      return
    }

    try {
      await onSubmit({
        type,
        title: title.trim(),
        description: description.trim(),
        targetType,
        targetId,
      })

      // Reset form
      setTitle('')
      setDescription('')
      setType(TicketType.ContentInappropriate)
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to submit report')
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null)
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Report Content</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Your report will be reviewed by our moderation team. Please provide as much detail as possible.
          </Typography>

          {contentPreview && (
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              <Typography variant="caption" fontWeight="bold">
                Reporting:
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {contentPreview.length > 100 ? `${contentPreview.slice(0, 100)}...` : contentPreview}
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Report Type */}
          <FormControl fullWidth size="small" disabled={isSubmitting}>
            <InputLabel>Report Type *</InputLabel>
            <Select value={type} onChange={(e) => setType(e.target.value as TicketType)} label="Report Type *">
              {contentReportTypes.map((ticketType) => (
                <MenuItem key={ticketType} value={ticketType}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TicketTypeIcon type={ticketType} size="small" />
                    {TicketTypeLabels[ticketType]}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Title */}
          <TextField
            fullWidth
            size="small"
            label="Title *"
            placeholder="Brief summary of the issue"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isSubmitting}
            inputProps={{ maxLength: 200 }}
            helperText={`${title.length}/200 characters`}
          />

          {/* Description */}
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label="Description *"
            placeholder="Please explain why you're reporting this content..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isSubmitting}
            size="small"
            inputProps={{ maxLength: 2000 }}
            helperText={`${description.length}/2000 characters`}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={isSubmitting || !title.trim() || !description.trim()}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Report'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
